import { reportClientError } from './clientErrorReporter';

const PERFORMANCE_SAMPLE_RATE = clampRate(process.env.REACT_APP_CLIENT_PERFORMANCE_SAMPLE_RATE, 0.05);
const SLOW_NAVIGATION_MS = 6000;
const SLOW_LCP_MS = 4000;
const POOR_CLS = 0.25;
const SLOW_INP_MS = 500;

let installed = false;
let performanceReported = false;
let latestLcp = 0;
let cumulativeLayoutShift = 0;
let worstInp = 0;

function clampRate(value, fallback) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return fallback;
  }
  return Math.min(1, Math.max(0, numeric));
}

function roundMetric(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.round(numeric) : 0;
}

function sanitizeText(value, maxLength = 180) {
  return String(value || '').trim().slice(0, maxLength);
}

function sanitizeResourceUrl(value) {
  const raw = sanitizeText(value, 500);
  if (!raw) {
    return '';
  }

  try {
    const parsed = new URL(raw, window.location.origin);
    return `${parsed.origin}${parsed.pathname}`.slice(0, 500);
  } catch (error) {
    return raw.split('?')[0].slice(0, 500);
  }
}

function getResourceTargetContext(target) {
  const tagName = sanitizeText(target?.tagName, 40).toLowerCase();
  if (!tagName || tagName === 'body' || tagName === 'html') {
    return null;
  }

  const source = target?.currentSrc || target?.src || target?.href || target?.poster || '';
  const resourceUrl = sanitizeResourceUrl(source);
  if (!resourceUrl) {
    return null;
  }

  return {
    tagName,
    resourceUrl,
    resourceType: sanitizeText(target?.rel || target?.type || target?.as || '', 80),
  };
}

function handleWindowError(event) {
  if (event?.target && event.target !== window) {
    return;
  }

  reportClientError('window_runtime_error', event?.error || event?.message || 'Window runtime error.', {
    source: sanitizeResourceUrl(event?.filename),
    line: Number(event?.lineno || 0),
    column: Number(event?.colno || 0),
  }, { force: true });
}

function handleUnhandledRejection(event) {
  reportClientError('unhandled_promise_rejection', event?.reason || 'Unhandled promise rejection.', {
    reasonType: sanitizeText(typeof event?.reason, 40),
  }, { force: true });
}

function handleResourceError(event) {
  const context = getResourceTargetContext(event?.target);
  if (!context) {
    return;
  }

  reportClientError('resource_load_error', new Error(`${context.tagName} failed to load.`), context);
}

function observeLargestContentfulPaint() {
  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      if (lastEntry) {
        latestLcp = lastEntry.startTime;
      }
    });
    observer.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch (error) {
    // Unsupported browser. Other monitoring still runs.
  }
}

function observeCumulativeLayoutShift() {
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!entry.hadRecentInput) {
          cumulativeLayoutShift += entry.value || 0;
        }
      }
    });
    observer.observe({ type: 'layout-shift', buffered: true });
  } catch (error) {
    // Unsupported browser. Other monitoring still runs.
  }
}

function observeInteractionToNextPaint() {
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.interactionId && entry.duration > worstInp) {
          worstInp = entry.duration;
        }
      }
    });
    observer.observe({ type: 'event', buffered: true, durationThreshold: 40 });
  } catch (error) {
    // Unsupported browser. Other monitoring still runs.
  }
}

function getNavigationMetrics() {
  const navigation = performance.getEntriesByType?.('navigation')?.[0];
  if (!navigation) {
    return {};
  }

  return {
    navigationDurationMs: roundMetric(navigation.duration),
    domContentLoadedMs: roundMetric(navigation.domContentLoadedEventEnd),
    responseEndMs: roundMetric(navigation.responseEnd),
    transferSize: roundMetric(navigation.transferSize),
  };
}

function reportPerformance(reason = 'timer') {
  if (performanceReported || typeof window === 'undefined' || !window.performance) {
    return;
  }

  performanceReported = true;
  const metrics = {
    ...getNavigationMetrics(),
    largestContentfulPaintMs: roundMetric(latestLcp),
    cumulativeLayoutShift: Number(cumulativeLayoutShift.toFixed(3)),
    interactionToNextPaintMs: roundMetric(worstInp),
    reason,
  };
  const slowPage = (
    metrics.navigationDurationMs > SLOW_NAVIGATION_MS
    || metrics.largestContentfulPaintMs > SLOW_LCP_MS
    || metrics.cumulativeLayoutShift > POOR_CLS
    || metrics.interactionToNextPaintMs > SLOW_INP_MS
  );

  if (!slowPage && Math.random() > PERFORMANCE_SAMPLE_RATE) {
    return;
  }

  reportClientError(
    slowPage ? 'page_performance_slow' : 'page_performance_sample',
    new Error(slowPage ? 'Page performance crossed a launch threshold.' : 'Sampled page performance.'),
    metrics,
    { force: slowPage, sampleRate: 1 }
  );
}

function installPerformanceMonitoring() {
  if (typeof PerformanceObserver === 'undefined' || typeof performance === 'undefined') {
    return;
  }

  observeLargestContentfulPaint();
  observeCumulativeLayoutShift();
  observeInteractionToNextPaint();
  window.setTimeout(() => reportPerformance('settled'), 8000);
  window.addEventListener('pagehide', () => reportPerformance('pagehide'), { once: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      reportPerformance('visibility_hidden');
    }
  }, { once: true });
}

export function installRuntimeMonitoring() {
  if (installed || typeof window === 'undefined') {
    return;
  }

  installed = true;
  window.addEventListener('error', handleWindowError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
  window.addEventListener('error', handleResourceError, true);
  installPerformanceMonitoring();
}
